import { useEffect } from 'react';

function useSwipe(containerRef, onSwipe, {
  swipeThresholdPx = 5,
  minDistancePx = 50,
  disabled,
  touchOnly,
  enabledDirections = "horisontal"
} = {}) {
  useEffect(() => {
    const container = containerRef.current;
    if (disabled || !container) {
      return;
    }
    const context = {
      startX: 0,
      startY: 0
    };
    const onPointerDown = evt => {
      if (touchOnly && evt.pointerType !== "touch") {
        return;
      }
      context.startX = evt.clientX;
      context.startY = evt.clientY;
      container.addEventListener("selectionchange", onPointerEnd);
      if (evt.pointerType === "touch") {
        container.addEventListener("touchmove", onPointerMove);
        container.addEventListener("touchend", onPointerEnd);
        container.addEventListener("touchcancel", onPointerEnd);
      } else {
        container.addEventListener("pointermove", onPointerMove);
        container.addEventListener("pointercancel", onPointerEnd);
        container.addEventListener("pointerleave", onPointerEnd);
        container.addEventListener("pointerup", onPointerEnd);
      }

      // In case a long press is performed, then a selection is probably being
      // made and hence we should not perform swipes.
      setTimeout(() => {
        onPointerEnd();
      }, 500);
    };
    const onPointerMove = evt => {
      if ("touches" in evt && evt.touches.length > 1) {
        // ignore touch events if the user applies more than one finger while
        // interacting (for instance if user is pinch zooming)
        return;
      }
      const clientX = "clientX" in evt ? evt.clientX : evt.touches[0]?.clientX ?? 0;
      const clientY = "clientY" in evt ? evt.clientY : evt.touches[0]?.clientY ?? 0;
      const distX = Math.abs(clientX - context.startX);
      const distY = Math.abs(clientY - context.startY);
      if (distY > swipeThresholdPx && enabledDirections === "vertical") {
        evt.preventDefault();
      }
      if (distX > swipeThresholdPx && enabledDirections === "horisontal") {
        evt.preventDefault();
      }
      if (distX > minDistancePx) {
        if (enabledDirections === "horisontal" || enabledDirections === "all") {
          onSwipe(clientX > context.startX ? "left-to-right" : "right-to-left");
        }
        onPointerEnd();
      }
      if (distY > minDistancePx) {
        if (enabledDirections === "vertical" || enabledDirections === "all") {
          onSwipe(clientY > context.startY ? "top-to-bottom" : "bottom-to-top");
        }
        onPointerEnd();
      }
    };
    const onPointerEnd = () => {
      container.removeEventListener("touchmove", onPointerMove);
      container.removeEventListener("touchend", onPointerEnd);
      container.removeEventListener("touchcancel", onPointerEnd);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointercancel", onPointerEnd);
      container.removeEventListener("pointerleave", onPointerEnd);
      container.removeEventListener("pointerup", onPointerEnd);
      container.removeEventListener("selectionchange", onPointerEnd);
    };
    container.addEventListener("pointerdown", onPointerDown);
    return () => {
      container.removeEventListener("touchmove", onPointerMove);
      container.removeEventListener("touchend", onPointerEnd);
      container.removeEventListener("touchcancel", onPointerEnd);
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointercancel", onPointerEnd);
      container.removeEventListener("pointerleave", onPointerEnd);
      container.removeEventListener("pointerup", onPointerEnd);
      container.removeEventListener("selectionchange", onPointerEnd);
    };
  }, [containerRef, minDistancePx, disabled, touchOnly, onSwipe, swipeThresholdPx, enabledDirections]);
}

export { useSwipe };
//# sourceMappingURL=swipe.js.map
